import { TFunction } from "i18next";
import * as yup from "yup";
import dayjs from "dayjs";
import { useBeneficiaryTermStore } from "../stores/beneficiaryTerm.store";

const createBeneficiarySchema = (
	t: TFunction,
	options: {
		isEligibility: boolean;
		displayBeneficiaryName: boolean;
		displayBeneficiaryAddress: boolean;
		displayBeneficiaryCMEligible: boolean;
		beneficiaryCINFormat: string;
		minAgeValidation: number;
		displayBeneficiaryDob: boolean;
		medicaidIdFormat?: string;
		gpraNumberFormat?: string;
		autogeneratedCIN?: boolean;
		isMultiTreatmentPlan?: boolean;
		isTreatmentPlanRequired?: boolean;
		// SIMS-5432 / SIMS-5412: effective email/phone requirement for this
		// enrollment. These carry the *resolved* requirement — the account
		// `emailMandatory` / `phoneMandatory` settings combined with the selected
		// program's mobile-validation requirement (which always forces email) —
		// not the raw settings. See RegisterBeneficiary for how they are derived.
		isEmailMandatory?: boolean;
		isPhoneMandatory?: boolean;
	},
) => {
	const today = dayjs();
	const minAgeDate = today
		.subtract(options.minAgeValidation || 0, "years")
		.endOf("day")
		.toDate();

	const schema = yup.object().shape({
		firstName: yup.string().when([], () => {
			return options.displayBeneficiaryName
				? yup
						.string()
						.required(t("validations.required_field", { field: "First name" }))
						.min(2, t("validations.min_length", { count: 2 }))
						.max(50, t("validations.max_length", { count: 50 }))
				: yup.string().optional();
		}),
		lastName: yup.string().when([], () => {
			return options.displayBeneficiaryName
				? yup
						.string()
						.required(t("validations.required_field", { field: "Last name" }))
						.min(2, t("validations.min_length", { count: 2 }))
						.max(50, t("validations.max_length", { count: 50 }))
				: yup.string().optional();
		}),
		dateOfBirth: yup.date().when([], () => {
			return options.displayBeneficiaryDob
				? yup
						.date()
						.required(
							t("validations.required_field", { field: "Date of birth" }),
						)
						.max(
							minAgeDate,
							options.minAgeValidation
								? `${useBeneficiaryTermStore.getState().singular || "Beneficiary"} must be at least ${options.minAgeValidation} years old`
								: "Date of birth cannot be in the future",
						)
				: yup.date().optional();
		}),

		email: yup
			.string()
			.email(t("validations.invalid_format"))
			.when([], () => {
				// SIMS-5432 / SIMS-5412: require email when the effective rule says
				// so (account emailMandatory, or a mobile-validation program which
				// always forces email). Keep the format check either way.
				return options.isEmailMandatory
					? yup
							.string()
							.email(t("validations.invalid_format"))
							.required(t("validations.required_field", { field: "Email" }))
					: yup.string().email(t("validations.invalid_format")).optional();
			}),
		// SIMS-5432: require phone when phoneMandatory is on. The format check must
		// apply in BOTH the required and optional branches — `.when()` REPLACES the
		// schema, so a `.test()` chained after it is dropped. Apply the format test
		// inside each returned branch instead. Empty is treated as valid by the
		// format test, so it never conflicts with `.required`.
		phoneNumber: yup.string().when([], () => {
			const withFormat = (s: yup.StringSchema): yup.StringSchema =>
				s.test("phoneNumber", t("validations.invalid_format"), (value) => {
					if (
						!value ||
						value.trim() === "" ||
						value === "+1" ||
						value === "+1 " ||
						value === "+44" ||
						value === "+44 " ||
						value === "+51" ||
						value === "+51 "
					) {
						return true;
					}

					let phoneRegex;
					if (value.startsWith("+1")) {
						phoneRegex = /^\+1\s?[-. ]?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
					} else if (value.startsWith("+44")) {
						phoneRegex = /^\+44\s?[-. ]?\(?\d{4}\)?[-. ]?\d{3}[-. ]?\d{3}$/;
					} else if (value.startsWith("+51")) {
						phoneRegex = /^\+51\s?[-. ]?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{3}$/;
					} else {
						phoneRegex = /^\+1\s?[-. ]?\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4}$/;
					}

					return phoneRegex.test(value);
				});

			return options.isPhoneMandatory
				? withFormat(
						yup
							.string()
							.required(
								t("validations.required_field", { field: "Phone number" }),
							),
					)
				: withFormat(yup.string().optional());
		}),
		addressLine1: yup.string().when([], () => {
			return options.displayBeneficiaryAddress
				? yup
						.string()
						.test("min", t("validations.min_length", { count: 10 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length >= 10;
						})
						.test("max", t("validations.max_length", { count: 250 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length < 250;
						})
				: yup.string().optional();
		}),
		addressLine2: yup.string().when([], () => {
			return options.displayBeneficiaryAddress
				? yup
						.string()
						.test("min", t("validations.min_length", { count: 10 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length >= 10;
						})
						.test("max", t("validations.max_length", { count: 250 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length < 250;
						})
				: yup.string().optional();
		}),
		city: yup.string().when([], () => {
			return options.displayBeneficiaryAddress
				? yup
						.string()
						.test("min", t("validations.min_length", { count: 3 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length >= 3;
						})
						.test("max", t("validations.max_length", { count: 250 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length < 250;
						})
				: yup.string().optional();
		}),
		state: yup.string().when([], () => {
			return options.displayBeneficiaryAddress
				? yup
						.string()
						.test("min", t("validations.min_length", { count: 5 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length >= 5;
						})
						.test("max", t("validations.max_length", { count: 250 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length < 250;
						})
				: yup.string().optional();
		}),
		zipCode: yup.string().when([], () => {
			return options.displayBeneficiaryAddress
				? yup
						.string()
						.test("min", t("validations.min_length", { count: 5 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length >= 5;
						})
						.test("max", t("validations.max_length", { count: 10 }), (val) => {
							if (!val || val.length === 0) {
								return true;
							}
							return val.length < 10;
						})
				: yup.string().optional();
		}),
		program: yup
			.string()
			.required(t("validations.required_field", { field: "Program" })),
		treatmentPlan: yup.string().when([], () => {
			return options.isMultiTreatmentPlan && options.isTreatmentPlanRequired
				? yup
						.string()
						.required(
							t("validations.required_field", { field: "Treatment Plan" }),
						)
				: yup.string().optional();
		}),
		treatmentConfigTemplate: yup.string().when([], () => {
			return options.isMultiTreatmentPlan && options.isTreatmentPlanRequired
				? yup.string().required(
						t("validations.required_field", {
							field: "Treatment Plan Template",
						}),
					)
				: yup.string().optional();
		}),
		// `treatmentConfigTemplate` is a SIBLING of treatmentConfig at the form root, so the custom
		// plan branch can be keyed off it directly rather than through validation context.
		treatmentConfig: yup
			.object()
			.when("treatmentConfigTemplate", ([template]) => {
				const isCustomPlan = template === "custom";
				return options.isMultiTreatmentPlan
					? yup.object().shape({
							visitsPerPeriod: yup.string().required(
								t("validations.required_field", {
									field: "Visits Per Period",
								}),
							),
							minHoursBetweenVisits: yup.string().optional(),
							duration: yup.string().required(
								t("validations.required_field", {
									field: "Program Duration",
								}),
							),
							behavior: yup.string().required(
								t("validations.required_field", {
									field: "Treatment Plan Input Type",
								}),
							),
							cmVisitSchedule: yup
								.mixed()
								.required(
									t("validations.required_field", { field: "Visit Schedule" }),
								),
							weeklyVisitsPerPeriod: yup.string().optional(),
							// REQUIRED on a custom plan, optional otherwise. Selecting "Custom Plan"
							// clears the template's rewardEngineId (TreatmentPlanFields — Mongoose
							// cannot cast "" to an ObjectId), and nothing else repopulates it: the
							// backend stores the config as sent and never backfills from the plan.
							// Left blank, the enrollment carries no engine at any level, and the
							// first visit in a segment that sets no override resolves `undefined` —
							// the app then posts `nameRule: "undefined.json"`, the rules engine
							// 500s, and the unhandled rejection crashes the page mid-submission.
							// Reproduced end-to-end against the deployed backend; invisible locally,
							// where applyRules is mocked and ignores nameRule entirely.
							rewardEngineId: isCustomPlan
								? yup.string().required(
										t("validations.required_field", {
											field: "Reward Engine",
										}),
									)
								: yup.string().optional(),
							cmVisitScheduleOptionIds: yup.string().optional(),
						})
					: yup.object().optional();
			}),
		providerSite: yup
			.string()
			.required(t("validations.required_field", { field: "Provider site" })),
		provider: yup
			.string()
			.required(t("validations.required_field", { field: "Provider" })),
		clientIDNumber: yup.string().when([], () => {
			return options.displayBeneficiaryDob &&
				options.autogeneratedCIN &&
				options.displayBeneficiaryName
				? yup
						.string()
						.required(
							t("validations.required_field", { field: "Client ID Number" }),
						)
				: yup
						.string()
						.required(
							t("validations.required_field", { field: "Client ID Number" }),
						)
						.test("regex", "Invalid format", (value) => {
							if (!value) {
								return true;
							}

							const regex = new RegExp(options.beneficiaryCINFormat);
							return regex.test(value);
						});
		}),
		cmVisitSchedule: yup.string().when([], () => {
			return options.isMultiTreatmentPlan
				? yup.string().optional()
				: yup
						.string()
						.required(
							t("validations.required_field", { field: "CM Visit schedule" }),
						);
		}),
		medicaidEligibility: yup.string().when([], () => {
			return options.isEligibility
				? yup.string().required(
						t("validations.required_field", {
							field: "Medicaid eligibility",
						}),
					)
				: yup.string();
		}),
		cmEligibility: yup.boolean().when([], () => {
			return options.displayBeneficiaryCMEligible
				? yup
						.boolean()
						.required(
							t("validations.required_field", { field: "CM Eligibility" }),
						)
				: yup.boolean();
		}),
	});

	return schema;
};

export default createBeneficiarySchema;
